/*
 * PDP: the size is only ever picked by the shopper (snippets/product-variant-picker.liquid renders
 * it unset unless the product has one size, even on a ?variant= link).
 * - Until a size is picked: the label reads "Size: Select a size", no size is ticked, the
 *   variant id the form would add is empty, Add to cart stays enabled (to show the prompt) and
 *   the URL carries no ?variant=.
 * - Changing colour never picks a size. Shopify answers a colour-only request with that
 *   colour's first available variant (usually S); that size is cleared again here. A size the
 *   shopper already chose is kept when it exists and is in stock in the new colour, otherwise it
 *   is cleared back to "Select a size". Price, images and the colour still follow the colour.
 * - Add to cart (also via the sticky bar, which clicks the same button) with no size: nothing
 *   is added; "Please select a size" shows under the sizes, the size row gives a short shake
 *   (transform only; none with reduced motion) and focus moves to the first size.
 */
(() => {
  const FIELD = 'fieldset[data-jc-size]';

  const rootOf = (el) => el.closest('product-info') || document;
  const sizeField = (root) => root.querySelector(FIELD);
  const chosen = (root) => root.dataset.jcSize || '';

  function idInputs(root) {
    const section = root.dataset.section;
    if (!section) return [];
    return [...document.querySelectorAll(`#product-form-${section} input[name="id"], #product-form-installment-${section} input[name="id"]`)];
  }

  /* back to "Select a size" */
  function applyUnset(root) {
    const f = sizeField(root);
    if (!f) return;
    delete root.dataset.jcSize;
    root.dataset.jcSizeSet = 'false';
    f.querySelectorAll('input[type="radio"]').forEach((i) => (i.checked = false));
    const label = f.querySelector('[data-selected-value]');
    if (label) {
      label.textContent = 'Select a size';
      label.classList.add('jc-optset__value--empty');
    }
    // no variant until a size is picked
    idInputs(root).forEach((input) => (input.value = ''));
    // keep Add to cart usable so the prompt can show
    const section = root.dataset.section;
    const btn = section ? document.getElementById(`ProductSubmitButton-${section}`) : null;
    if (btn) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      const span = btn.querySelector('span');
      if (span && window.variantStrings) span.textContent = window.variantStrings.addToCart;
    }
    // no ?variant= in the address until a size is chosen
    const url = new URL(window.location.href);
    if (url.searchParams.has('variant')) {
      url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url.pathname + (url.search || '') + url.hash);
    }
  }

  function showChosen(f, value) {
    const label = f.querySelector('[data-selected-value]');
    if (!label) return;
    label.textContent = value;
    label.classList.remove('jc-optset__value--empty');
  }

  /* after every variant change: keep the shopper's size if the new colour has it in stock. The
     re-rendered picker never ticks a size (the server never preselects one), so the shopper's own
     pick is ticked again here; the variant id was already set by the theme for that combination. */
  function reconcile(root) {
    const f = sizeField(root);
    if (!f) return;
    const want = chosen(root);
    // the shopper just picked this size themselves (even a sold-out one): keep it
    const justPicked = root.dataset.jcJustPicked === '1';
    delete root.dataset.jcJustPicked;
    if (want) {
      const input = [...f.querySelectorAll('input[type="radio"]')].find((i) => i.value === want);
      const inStock = input && !input.disabled && !input.classList.contains('disabled');
      if (input && (justPicked || inStock)) {
        input.checked = true;
        root.dataset.jcSizeSet = 'true';
        showChosen(f, want);
        return;
      }
    }
    applyUnset(root);
  }

  function showMessage(root) {
    const f = sizeField(root);
    if (!f) return;
    const msg = f.querySelector('[data-jc-size-msg]');
    if (msg) msg.hidden = false;
    f.classList.remove('jc-shake');
    void f.offsetWidth; // restart the shake
    f.classList.add('jc-shake');
    const first = f.querySelector('input[type="radio"]:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }

  function init() {
    document.querySelectorAll('product-info').forEach((root) => {
      const f = sizeField(root);
      if (!f) return;
      const preset = f.dataset.jcSizeSet === 'true' ? f.querySelector('input[type="radio"]:checked') : null;
      if (preset) {
        root.dataset.jcSize = preset.value;
        root.dataset.jcSizeSet = 'true';
      } else {
        applyUnset(root);
      }
    });
  }

  // the shopper picks a size: remember it; the theme's normal variant flow takes over
  document.addEventListener('change', (e) => {
    const f = e.target.closest && e.target.closest(FIELD);
    if (!f || e.target.type !== 'radio') {
      // any other option (colour): not a size pick
      const other = e.target.closest && e.target.closest('variant-selects');
      if (other) delete rootOf(other).dataset.jcJustPicked;
      return;
    }
    const root = rootOf(f);
    root.dataset.jcSize = e.target.value;
    root.dataset.jcSizeSet = 'true';
    root.dataset.jcJustPicked = '1';
    const msg = f.querySelector('[data-jc-size-msg]');
    if (msg) msg.hidden = true;
    const label = f.querySelector('[data-selected-value]');
    if (label) label.classList.remove('jc-optset__value--empty');
  });

  // Add to cart with no size: stop it before the theme's form handler runs
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      const root = form.closest('product-info') || document.querySelector(`product-info[data-section="${(form.id || '').replace('product-form-', '')}"]`);
      if (!root || !sizeField(root) || chosen(root)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      showMessage(root);
    },
    true
  );

  document.addEventListener('animationend', (e) => {
    if (e.animationName === 'jc-shake') e.target.classList.remove('jc-shake');
  });

  // PUB_SUB_EVENTS is a top-level const in constants.js, so it is not on window: check it by name
  const hook = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      document.querySelectorAll('product-info').forEach((root) => {
        if (data && data.sectionId && root.dataset.section && data.sectionId !== root.dataset.section && root.dataset.originalSection !== data.sectionId) return;
        reconcile(root);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      hook();
    });
  } else {
    init();
    hook();
  }
})();
